import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { getToken, setToken, clearToken } from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const refresh = useCallback(async () => {
        if (!getToken()) {
            setUser(null);
            return null;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
            return data;
        } catch {
            clearToken();
            setUser(null);
            return null;
        }
    }, []);

    useEffect(() => {
        refresh().finally(() => setLoading(false));
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        // New shape: { access_token, token_type, user }
        // Old shape (backwards-compat): the user object directly.
        if (data?.access_token) {
            setToken(data.access_token);
            setUser(data.user);
            return data.user;
        }
        setUser(data);
        return data;
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } finally {
            clearToken();
            setUser(null);
        }
    }, []);

    const changePassword = useCallback(async (currentPassword, newPassword) => {
        const { data } = await api.post("/auth/change-password", {
            current_password: currentPassword,
            new_password: newPassword,
        });
        if (data?.access_token) {
            setToken(data.access_token);
        }
        await refresh();
    }, [refresh]);

    return (
        <AuthContext.Provider value={{ user, loading, login, logout, refresh, changePassword }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
};
